import React, {useState} from 'react'

import {returnRefundMethods} from '../../../../utils/variables.js'
import {Button} from '../../../components/UI/index.js'
import {AddressLines} from './ReturnParcelOverview.js'
import {DetailsStep, ItemsStep, RefundStep, ShippingStep} from './requestSteps.js'
import {TEXT, InfoSection, StatusBanner, StepTitle, Stepper, carrierDisplayName, formatCosts} from './ui.js'
import {formSpacing} from '../../../components/UI/forms/index.js'

const SummaryStep = {key: 'summary', label: 'Overzicht', isValid: () => true}

// Guides the consumer through a new return request and submits it. On success the created return parcel
// is passed to onCreated, when it has to be paid the consumer is sent to the payment page first.
export default function ReturnRequestFlow({portal, color, initialRequest, options, onSubmit, onCreated, onPay}) {
    const [request, setRequest] = useState(initialRequest)
    const [stepIndex, setStepIndex] = useState(0)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')

    const steps = [
        DetailsStep,
        request.items.length ? ItemsStep : null,
        portal.returnRefundMethods.length > 1 || portal.returnRefundMethods[0] === 'exchange' ? RefundStep : null,
        ShippingStep,
        SummaryStep
    ].filter(Boolean)

    const step = steps[stepIndex]
    const carrier = options.carriers.find((carrier) => carrier.name === request.carrier)

    const goTo = (key) => setStepIndex(steps.findIndex((step) => step.key === key))

    const submit = async () => {
        setSubmitting(true)
        setError('')

        try {
            const returnParcel = await onSubmit(request)

            if (returnParcel.label.status === 'awaitingPayment') {
                try {
                    await onPay() // redirects to the payment page
                    return
                } catch (err) {
                    console.error(err) // the overview shows the pay button
                }
            }

            onCreated(returnParcel)
        } catch (err) {
            setError(err.message)
            setSubmitting(false)
        }
    }

    return (
        <div>
            <Stepper steps={steps} current={stepIndex} color={color} />

            <div key={step.key} className='rp-fade-in'>
                {step === SummaryStep ?
                    <Summary portal={portal} request={request} carrier={carrier} goTo={goTo} /> :
                    <step.Component portal={portal} color={color} options={options} request={request} onChange={setRequest} />
                }
            </div>

            {error &&
                <StatusBanner tone='error' title='Aanmelden is niet gelukt' style={{marginTop: 16}}>
                    {error}
                    <div style={{marginTop: 8}}>
                        <button className='rp-link' onClick={() => window.location.reload()}>Pagina vernieuwen</button>
                    </div>
                </StatusBanner>
            }

            <div style={{display: 'flex', columnGap: formSpacing.columnGap, marginTop: 24}}>
                {stepIndex > 0 &&
                    <Button
                        variant='outline'
                        color={color}
                        style={{flex: 1}}
                        disabled={submitting}
                        label='Vorige'
                        onClick={() => setStepIndex(stepIndex - 1)}
                    />
                }

                {step === SummaryStep ?
                    <Button
                        variant='solid'
                        color={color}
                        style={{flex: 2}}
                        loading={submitting}
                        label={carrier?.paymentRequired ? `Aanmelden en ${formatCosts(carrier.shippingCosts)} betalen` : 'Retour aanmelden'}
                        onClick={submit}
                    /> :
                    <Button
                        variant='solid'
                        color={color}
                        style={{flex: 2}}
                        disabled={!step.isValid(request, portal)}
                        label='Volgende'
                        onClick={() => setStepIndex(stepIndex + 1)}
                    />
                }
            </div>
        </div>
    )
}

function Summary({portal, request, carrier, goTo}) {
    const returnedItems = request.items.filter((item) => item.return)
    const refundMethod = returnRefundMethods[request.refundMethod]

    return (
        <>
            <StepTitle title='Controleer je retour' subtitle='Klopt alles? Meld je retour dan aan.' />

            <InfoSection title='Jouw gegevens' onEdit={() => goTo('details')}>
                <AddressLines address={request.address} />
            </InfoSection>

            {!!returnedItems.length &&
                <InfoSection title='Items' onEdit={() => goTo('items')}>
                    {returnedItems.map((item, index) => (
                        <div key={index} style={{display: 'flex', gap: 12, padding: '2px 0'}}>
                            <div style={{flex: 1}}>
                                {item.description}
                                {item.returnReason && <div style={{fontSize: 13, color: TEXT.muted}}>{`${item.returnReason} ${item.returnReasonComment || ''}`}</div>}
                            </div>
                            <div style={{color: TEXT.muted}}>{item.returnQuantity}×</div>
                        </div>
                    ))}
                </InfoSection>
            }

            {refundMethod &&
                <InfoSection title='Terugbetaling' onEdit={portal.returnRefundMethods.length > 1 || request.refundMethod === 'exchange' ? () => goTo('refund') : undefined}>
                    {refundMethod.title}
                    {request.refundMethod === 'exchange' && <div style={{fontSize: 13, color: TEXT.muted}}>{request.refundMethodComment}</div>}
                </InfoSection>
            }

            <InfoSection title='Verzending' onEdit={() => goTo('shipping')}>
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <span>{carrierDisplayName(request.carrier)} retourzending</span>
                    <span style={{fontWeight: 500}}>{formatCosts(carrier?.shippingCosts)}</span>
                </div>
            </InfoSection>
        </>
    )
}
